import {
  Box,
  Form,
  FormGroup,
  InlineMessage,
  Input,
  Select,
  Small,
} from '@bigcommerce/big-design';

import type { Channel, Currency, LocationFormData, PickupMethod, TaxZone } from './types';

interface GeneralSettingsTabProps {
  formData: LocationFormData;
  onChange: (data: Partial<LocationFormData>) => void;
  currencies: Currency[];
  taxZones: TaxZone[];
  channels: Channel[];
  pickupMethods: PickupMethod[];
  storeChannelId: number;
  locationLabel?: string;
  locationId?: number;
}

export function GeneralSettingsTab({
  formData,
  onChange,
  currencies,
  taxZones,
  channels,
  pickupMethods,
  storeChannelId,
  locationLabel,
  locationId,
}: GeneralSettingsTabProps) {
  // Filter to only enabled channels (allow channel 1 for flexibility)
  const availableChannels = channels.filter(c => c.isEnabled);
  const storeChannel = channels.find(c => c.id === storeChannelId);
  const selectedChannel = formData.bcChannelId
    ? channels.find(c => c.id === formData.bcChannelId)
    : storeChannel;
  const isUsingNonPosChannel = selectedChannel && selectedChannel.type !== 'pos';
  const isUsingDefaultStorefront = formData.bcChannelId === 1 || (!formData.bcChannelId && storeChannelId === 1);
  return (
    <Form>
      <FormGroup>
        <Input
          label="Display Name"
          description="Name shown on POS devices and receipts"
          placeholder={locationLabel || 'Store Location'}
          value={formData.displayName}
          onChange={(e) => onChange({ displayName: e.target.value })}
          required
        />
      </FormGroup>
      <FormGroup>
        {currencies.length === 0 ? (
          <InlineMessage
            type="warning"
            messages={[{
              text: 'No currencies available. Add currencies to your POS channel in BigCommerce Settings → Currencies.',
            }]}
          />
        ) : (
          <>
            <Select
              label="Currency"
              description="This determines pricing and payment currency for this location."
              options={currencies.map(c => ({
                value: c.code,
                content: `${c.code} - ${c.name}${c.isDefault ? ' (Store Default)' : ''}`,
              }))}
              value={formData.currency}
              onOptionChange={(value) =>
                value && onChange({ currency: value })
              }
              required
            />
            {formData.currency && !currencies.find(c => c.code === formData.currency && c.isDefault) && (
              <InlineMessage
                type="info"
                marginTop="small"
                messages={[{
                  text: `Using ${formData.currency} instead of store default. BigCommerce will convert prices using exchange rates, or use Price Lists if configured.`,
                }]}
              />
            )}
          </>
        )}
      </FormGroup>
      <FormGroup>
        {taxZones.length === 0 ? (
          <InlineMessage
            type="warning"
            messages={[{
              text: 'No tax zones available. Set up tax zones in BigCommerce Settings → Tax.',
            }]}
          />
        ) : (
          <Select
            label="Tax Zone"
            description="From your BigCommerce tax zones. Used for offline tax calculations."
            options={[
              { value: '', content: 'Select a tax zone...' },
              ...taxZones.map(z => ({
                value: z.id.toString(),
                content: z.name,
              })),
            ]}
            value={formData.taxZoneId?.toString() || ''}
            onOptionChange={(value) =>
              onChange({ taxZoneId: value ? parseInt(value, 10) : null })
            }
          />
        )}
      </FormGroup>
      <FormGroup>
        <Box marginBottom="xSmall">
          <Select
            label="Pickup Method"
            description="Required for accurate tax calculation on POS orders. Creates a pickup consignment in BigCommerce."
            options={[
              { value: '', content: 'Select a pickup method...' },
              ...pickupMethods.map(m => ({
                value: m.id.toString(),
                content: `${m.displayName}${m.locationId === locationId ? ' (This location)' : ''}`,
              })),
            ]}
            value={formData.pickupMethodId?.toString() || ''}
            onOptionChange={(value) =>
              onChange({ pickupMethodId: value ? parseInt(value, 10) : null })
            }
            required
          />
        </Box>
        {!formData.pickupMethodId && (
          <InlineMessage
            type="warning"
            messages={[{
              text: 'A pickup method is required. Without it, taxes may not calculate correctly for POS orders.',
            }]}
          />
        )}
        {pickupMethods.length === 0 && (
          <InlineMessage
            type="info"
            messages={[{
              text: 'No pickup methods found. They will be auto-created when you save this location.',
            }]}
          />
        )}
      </FormGroup>
      {availableChannels.length > 0 && (
        <FormGroup>
          <Select
            label="Channel Override"
            description={`Override the store's default channel for this location. Leave as default to use the store channel${storeChannel ? ` (${storeChannel.name})` : ''}.`}
            options={[
              { value: '', content: `Use store default${storeChannel ? ` (${storeChannel.name})` : ''}` },
              ...availableChannels.map(c => ({
                value: c.id.toString(),
                content: `${c.name} (${c.type})${c.type === 'pos' ? ' ✓' : ''}`,
              })),
            ]}
            value={formData.bcChannelId?.toString() || ''}
            onOptionChange={(value) =>
              onChange({ bcChannelId: value ? parseInt(value, 10) : null })
            }
          />
          <Small>
            This channel will be used for orders and promotions at this location.
          </Small>
          {isUsingDefaultStorefront && (
            <InlineMessage
              type="info"
              marginTop="small"
              messages={[{
                text: 'Channel 1 is your default storefront. Using a dedicated POS channel is recommended to keep in-store and online orders separate.',
              }]}
            />
          )}
          {isUsingNonPosChannel && !isUsingDefaultStorefront && (
            <InlineMessage
              type="info"
              marginTop="small"
              messages={[{
                text: `Using a ${selectedChannel?.type} channel. A dedicated POS channel is recommended but not required.`,
              }]}
            />
          )}
        </FormGroup>
      )}
    </Form>
  );
}
